import { useState } from 'react';
import { Button, capitalize, Stack, Typography, useTheme } from '@mui/material';
import { StripeSummary } from '../../types/stripe';
import { formatDollarAmount } from '../../utils/global/helper';
import useStripeSummary from '../../hooks/stripe/useStripeSummary';
import useConfirm from '../../hooks/useConfirm';
import {
  INSTANT_PAYOUT_LIMITED_SUBTITLE,
  INSTANT_PAYOUT_LIMITED_TITLE,
} from '../../helpers/payments';
import InstantPayoutModal from './InstantPayoutModal';

interface Props {
  summary: StripeSummary;
}

function StripeAccountBalance({ summary }: Props) {
  const theme = useTheme();
  const { isFetching } = useStripeSummary();
  const confirm = useConfirm();

  const [isInstantPayoutModalOpen, setIsInstantPayoutModalOpen] =
    useState(false);

  const totalBalance = (summary.balance.total || 0) / 100;
  const instantAvailable = (summary.balance.instant || 0) / 100;
  const instantPayoutDisabled =
    instantAvailable <= 0 ||
    isFetching ||
    !summary.instantEligibleExternalAccount ||
    summary.balance.instant < summary.minimumAmount;

  const onOpenInstantPayout = async () => {
    if (summary.isAccountUnderTwoDaysOld) {
      await confirm({
        title: INSTANT_PAYOUT_LIMITED_TITLE,
        message: INSTANT_PAYOUT_LIMITED_SUBTITLE,
        negativeText: '',
        positiveText: 'I understand',
      });
      return;
    }
    setIsInstantPayoutModalOpen(true);
  };

  return (
    <Stack
      bgcolor={theme.palette.lightGrey.main}
      p={2}
      borderRadius="5px"
      direction="row"
      justifyContent="space-between"
      gap={1}>
      <Stack gap={1}>
        <Typography variant="body2" color="text.primary">
          Total Balance
        </Typography>
        <Typography variant="h1" component="p" color="text.primary">
          {formatDollarAmount(totalBalance)}
        </Typography>
        <Typography variant="body2" component="p" color="text.secondary">
          {summary.payoutInterval !== 'manual'
            ? `${capitalize(
                summary.payoutInterval || 'daily',
              )} automatic payouts`
            : 'Manual payouts only'}
        </Typography>
      </Stack>

      <Stack gap={1} my="auto">
        <Button
          variant="base"
          color="primary"
          disabled={instantPayoutDisabled}
          onClick={onOpenInstantPayout}>
          Instant Payout
        </Button>
      </Stack>

      {isInstantPayoutModalOpen && (
        <InstantPayoutModal
          summary={summary}
          open
          onClose={() => setIsInstantPayoutModalOpen(false)}
        />
      )}
    </Stack>
  );
}

export default StripeAccountBalance;
